import React, { forwardRef, useMemo } from 'react';
import { CircularProgress, FormControl, FormHelperText, InputLabel } from '@mui/material';
import ReactSelect, { MultiValue, SingleValue } from 'react-select';
import useGroups from 'views/App/Portal/LexID/Groups/_data_sources/useGroups';
import { Group } from 'views/App/Portal/LexID/Groups/_types/group';

interface GroupOption {
  value: string;
  label: string;
  group: Group;
}

export type GroupPickerProps = {
  allowBlank?: boolean;
  allowMultiple?: boolean;
  id: string;
  label?: string;
  value?: string[];
  onChange: (value: string[]) => void;
  organizationId: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string | undefined;
  size?: string;
};

const GroupPicker = forwardRef(
  (
    {
      allowBlank = false,
      allowMultiple = false,
      id,
      label,
      value = [],
      onChange,
      organizationId,
      placeholder,
      disabled = false,
      required,
      error,
      helperText,
      size,
    }: GroupPickerProps,
    ref
  ) => {
    const groupsQuery = useGroups(organizationId, { size: 100 });

    const groupOptions = useMemo(() => {
      try {
        return [
          ...(allowBlank ? [{ value: '', label: placeholder ?? '-- All Groups --' }] : []),
          ...(groupsQuery.data?.pages[0]?.content ?? []).map((x) => {
            return { value: x.id!, label: `${x.name ?? ''}`, group: x };
          }),
        ];
      } catch (e) {
        return [];
      }
    }, [groupsQuery.data, allowBlank]);

    const selectedOptions = useMemo(() => {
      return groupOptions.filter((x) => (value ?? []).includes(x.value));
    }, [value, groupOptions]);

    return groupsQuery.data?.pages[0]?.content ? (
      <FormControl
        key={id}
        fullWidth
        error={!!error}
        sx={{
          p: 0,
          paddingTop: !!label ? '1rem' : 0,
        }}
      >
        {!!label && (
          <InputLabel
            required={required}
            sx={{
              fontSize: '0.95rem',
              shrink: {
                transform: 'translate(0px, -8px) scale(1) !important',
                backgroundColor: 'transparent',
              },
            }}
          >
            {label}
          </InputLabel>
        )}
        <ReactSelect
          name={id}
          value={selectedOptions}
          options={groupOptions}
          formatOptionLabel={(option) => <span title={option.label}>{option.label}</span>}
          placeholder={placeholder}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, width: 300 }),
            option: (base) => ({
              ...base,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }),
            singleValue: (base) => ({
              ...base,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '90%',
            }),
            multiValueLabel: (base) => ({
              ...base,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: 220,
            }),
            input: (base) => ({
              ...base,
              boxShadow: 'none',
              outline: 0,
              minHeight: size === 'small' ? 26 : 45,
            }),
          }}
          menuPosition='fixed'
          menuPlacement='auto'
          isMulti={allowMultiple}
          isSearchable
          isDisabled={disabled}
          onChange={(newValue) => {
            if (Array.isArray(newValue)) {
              const newVals = newValue as MultiValue<GroupOption>;
              onChange((newValue ?? []).map((x) => x.value));
            } else {
              const newVal = newValue as SingleValue<GroupOption>;
              onChange(!!newVal ? [newVal.value] : []);
            }
          }}
        />
        {!!error && <FormHelperText error>{error.message}</FormHelperText>}
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    ) : (
      <CircularProgress />
    );
  }
);

export default GroupPicker;
